import type { CVData } from "../types";

export function CVTemplateClassic({ data }: { data: CVData }) {
  const { profile, experiences, education, skills, certificates } = data;

  return (
    <div
      className="cv-content mx-auto max-w-[800px] bg-[#fafaf9] p-10 text-gray-800 print:p-8"
      style={{
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: 12,
        lineHeight: 1.6,
      }}
    >
      <div className="border-b-2 border-gray-800 pb-4 text-center">
        <h1 className="text-2xl font-bold text-gray-900">{profile.name}</h1>
        <p className="mt-1 text-sm text-gray-600">{profile.title}</p>
        <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-0 text-xs text-gray-500">
          {profile.email && <span>{profile.email}</span>}
          {profile.phone && <span>|</span>}
          {profile.phone && <span>{profile.phone}</span>}
          {profile.address && <span>|</span>}
          {profile.address && <span>{profile.address}</span>}
        </div>
      </div>

      {profile.bio && (
        <section className="mt-5">
          <h2 className="border-b border-gray-400 pb-1 text-sm font-bold uppercase tracking-wider text-gray-800">
            Professional Summary
          </h2>
          <p className="mt-2 text-justify indent-4">{profile.bio}</p>
        </section>
      )}

      {experiences.length > 0 && (
        <section className="mt-5">
          <h2 className="border-b border-gray-400 pb-1 text-sm font-bold uppercase tracking-wider text-gray-800">
            Professional Experience
          </h2>
          <div className="mt-3 space-y-4">
            {experiences.map((exp) => (
              <div key={exp.id}>
                <div className="flex justify-between">
                  <span className="font-bold text-gray-900">{exp.position}</span>
                  <span className="text-xs text-gray-600 italic">
                    {exp.period_start} – {exp.period_end || "Present"}
                  </span>
                </div>
                <p className="text-sm text-gray-700 italic">{exp.company}</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-5">
                  {exp.responsibilities.map((r, i) => (
                    <li key={i} className="text-justify">{r}</li>
                  ))}
                </ul>
                {exp.tech_stack.length > 0 && (
                  <p className="mt-1 text-xs text-gray-600">
                    <span className="font-semibold">Technologies:</span>{" "}
                    {exp.tech_stack.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {education.length > 0 && (
        <section className="mt-5">
          <h2 className="border-b border-gray-400 pb-1 text-sm font-bold uppercase tracking-wider text-gray-800">
            Education
          </h2>
          <div className="mt-3 space-y-2">
            {education.map((edu) => (
              <div key={edu.id} className="flex justify-between">
                <div>
                  <span className="font-bold text-gray-900">{edu.school}</span>
                  <span className="text-gray-700"> — {edu.degree}</span>
                  {edu.grade && (
                    <span className="text-gray-600">, {edu.grade}</span>
                  )}
                </div>
                <span className="text-xs text-gray-600">{edu.period}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {skills.length > 0 && (
        <section className="mt-5">
          <h2 className="border-b border-gray-400 pb-1 text-sm font-bold uppercase tracking-wider text-gray-800">
            Technical Skills
          </h2>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1">
            {skills.map((s) => (
              <div key={s.id} className="flex justify-between text-sm">
                <span>{s.name}</span>
                <span className="text-gray-600">
                  {s.level >= 80 ? "Expert" : s.level >= 60 ? "Proficient" : s.level >= 40 ? "Intermediate" : "Beginner"}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {certificates.length > 0 && (
        <section className="mt-5">
          <h2 className="border-b border-gray-400 pb-1 text-sm font-bold uppercase tracking-wider text-gray-800">
            Certifications
          </h2>
          <div className="mt-3 space-y-1">
            {certificates.map((c) => (
              <div key={c.id} className="text-sm">
                <strong>{c.name}</strong>
                {c.description && (
                  <span className="text-gray-600"> — {c.description}</span>
                )}
                {c.date && (
                  <span className="text-gray-500"> ({c.date})</span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
